/**
 * Decorations hanging from the top edge: festival bunting and jungle vines
 * (with an optional leafy canopy fringe). Both sway gently.
 */
import type { BuntingLayer, VinesLayer } from '@dropstack/core';
import { spreadPositions, type SceneSize } from '../layout.ts';
import { hash01, range, rngFor } from '../noise.ts';
import { bodyGradient, circleBlob, circlePath, leafPath, pick, quadPoint, rgba, rimOf, shade, strokeRim, type Circle, type Ctx, type Point } from './draw.ts';
import type { Painter } from './types.ts';

// ───────────── bunting ─────────────

interface Swag {
  a: Point;
  c: Point;
  b: Point;
}

/** One string of bunting is a chain of swags about a screen-unit wide. */
function swags(layer: BuntingLayer, row: number, size: SceneSize): Swag[] {
  const y = (layer.y + row * 0.055) * size.height;
  const sag = layer.sag * size.height * (1 - row * 0.25);
  const x0 = -size.bleed - 4;
  const x1 = size.width + size.bleed + 4;
  const n = Math.max(1, Math.round((x1 - x0) / (size.unit * 0.9)));
  const w = (x1 - x0) / n;
  return Array.from({ length: n }, (_, i) => {
    const a = { x: x0 + i * w, y };
    const b = { x: a.x + w, y };
    return { a, b, c: { x: a.x + w / 2, y: y + 2 * sag } };
  });
}

export const buntingPainter: Painter<BuntingLayer> = {
  extent: (layer, { size }) => ({
    top: 0,
    bottom: (layer.y + (layer.rows ?? 1) * 0.055 + layer.sag) * size.height + size.unit * 0.08,
  }),
  moving(ctx, layer, { size, time }) {
    const rows = Math.max(1, Math.floor(layer.rows ?? 1));
    const colors = layer.colors.length ? layer.colors : ['#ff6b6b', '#ffd43b', '#4dabf7'];
    const string = layer.string ?? '#ffffff';
    const fw = size.unit * 0.032;
    const fh = size.unit * 0.048;
    for (let r = 0; r < rows; r++) {
      const chain = swags(layer, r, size);
      ctx.beginPath();
      for (const s of chain) {
        ctx.moveTo(s.a.x, s.a.y);
        ctx.quadraticCurveTo(s.c.x, s.c.y, s.b.x, s.b.y);
      }
      ctx.lineCap = 'round';
      ctx.strokeStyle = rgba(shade(string, -0.35), 0.7);
      ctx.lineWidth = Math.max(1.5, size.unit * 0.005);
      ctx.stroke();
      ctx.strokeStyle = string;
      ctx.lineWidth = Math.max(1, size.unit * 0.003);
      ctx.stroke();
      let k = r * 2;
      for (const s of chain) {
        const len = Math.hypot(s.b.x - s.a.x, s.b.y - s.a.y);
        const flags = Math.max(2, Math.round(len / (fw * 1.45)));
        for (let i = 0; i < flags; i++, k++) {
          const t = (i + 0.5) / flags;
          const p = quadPoint(s.a, s.c, s.b, t);
          const q = quadPoint(s.a, s.c, s.b, Math.min(1, t + 0.02));
          const ang = Math.atan2(q.y - p.y, q.x - p.x);
          const flutter = Math.sin(time * 2 + k * 0.7) * fw * 0.18;
          const color = pick(colors, k);
          ctx.save();
          ctx.translate(p.x, p.y);
          ctx.rotate(ang);
          ctx.beginPath();
          ctx.moveTo(-fw / 2, 0);
          ctx.lineTo(fw / 2, 0);
          ctx.quadraticCurveTo(fw * 0.2, fh * 0.55, flutter, fh);
          ctx.quadraticCurveTo(-fw * 0.2, fh * 0.55, -fw / 2, 0);
          ctx.closePath();
          ctx.fillStyle = bodyGradient(ctx, color, 0, fh, 0.15, -0.08);
          ctx.fill();
          strokeRim(ctx, rimOf(color), size, 0.7);
          ctx.beginPath();
          ctx.moveTo(-fw * 0.3, fh * 0.1);
          ctx.lineTo(-fw * 0.05, fh * 0.1);
          ctx.lineTo(flutter * 0.4 - fw * 0.08, fh * 0.55);
          ctx.closePath();
          ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
          ctx.fill();
          ctx.restore();
        }
      }
    }
  },
};

// ───────────── vines ─────────────

interface Vine {
  x: number;
  len: number;
  phase: number;
  seed: number;
}

function planVines(layer: VinesLayer, size: SceneSize, seed: number): Vine[] {
  const rng = rngFor(seed, 1);
  const xs = spreadPositions(layer.count, [-0.02, 1.02], (i) => hash01(seed + 2, i));
  return xs.map((fx, i) => {
    // Shorter towards the middle, so the vines frame the playfield rather than cover it.
    const edge = Math.min(1, Math.abs(fx - 0.5) * 2);
    return {
      x: fx * size.width,
      len: layer.length * size.height * range(rng, 0.55, 1) * (0.45 + 0.55 * edge),
      phase: range(rng, 0, Math.PI * 2),
      seed: (seed + i * 97) >>> 0,
    };
  });
}

/** Points down a vine, curling a little and swinging like a pendulum. */
function vinePoints(v: Vine, size: SceneSize, time: number): Point[] {
  const swing = Math.sin(time * 0.6 + v.phase) * 0.05;
  const curl = size.unit * 0.018;
  return Array.from({ length: 12 }, (_, i) => {
    const t = i / 11;
    return { x: v.x + Math.sin(t * Math.PI * 1.6 + v.phase) * curl * t + swing * v.len * t * t, y: t * v.len };
  });
}

function flower(ctx: Ctx, x: number, y: number, r: number, color: string): void {
  ctx.beginPath();
  for (let k = 0; k < 5; k++) {
    const a = (k / 5) * Math.PI * 2 - Math.PI / 2;
    circlePath(ctx, x + Math.cos(a) * r * 0.6, y + Math.sin(a) * r * 0.6, r * 0.48);
  }
  ctx.fillStyle = color;
  ctx.fill();
  ctx.beginPath();
  circlePath(ctx, x, y, r * 0.32);
  ctx.fillStyle = '#ffe066';
  ctx.fill();
}

export const vinesPainter: Painter<VinesLayer> = {
  extent: (layer, { size }) => ({ top: 0, bottom: layer.length * size.height + size.unit * 0.06 }),
  // The leafy fringe along the top never moves.
  still(ctx, layer, { size, seed }) {
    if (!layer.canopy) return;
    const rng = rngFor(seed, 5);
    const circles: Circle[] = [];
    for (let x = -size.bleed - size.unit * 0.04; x < size.width + size.bleed + size.unit * 0.06; ) {
      const r = size.unit * range(rng, 0.04, 0.07);
      circles.push({ x, y: -r * 0.25 + size.unit * range(rng, 0, 0.02), r });
      x += r * 1.25;
    }
    circleBlob(ctx, circles, bodyGradient(ctx, layer.canopy, 0, size.unit * 0.09, 0.08, -0.12), rimOf(layer.canopy), size);
    ctx.beginPath();
    for (const c of circles) leafPath(ctx, c.x + c.r * 0.2, c.y + c.r * 0.55, c.r * 0.7, c.r * 0.2, 1.2 + (c.x % 7) * 0.1);
    ctx.fillStyle = rgba(shade(layer.canopy, 0.25), 0.6);
    ctx.fill();
  },
  moving(ctx, layer, { size, time, seed }) {
    const leafLen = size.unit * 0.036;
    for (const v of planVines(layer, size, seed)) {
      const pts = vinePoints(v, size, time);
      ctx.beginPath();
      ctx.moveTo(pts[0]!.x, pts[0]!.y);
      for (let i = 1; i < pts.length - 1; i++) {
        const p = pts[i]!;
        const n = pts[i + 1]!;
        ctx.quadraticCurveTo(p.x, p.y, (p.x + n.x) / 2, (p.y + n.y) / 2);
      }
      ctx.lineCap = 'round';
      ctx.strokeStyle = shade(layer.color, -0.25);
      ctx.lineWidth = Math.max(2, size.unit * 0.009);
      ctx.stroke();
      ctx.strokeStyle = layer.color;
      ctx.lineWidth = Math.max(1, size.unit * 0.005);
      ctx.stroke();
      // Leaves along the vine, alternating sides.
      ctx.beginPath();
      pts.forEach((p, i) => {
        if (i === 0) return;
        const side = i % 2 ? 1 : -1;
        const a = Math.PI / 2 - side * 0.9;
        leafPath(ctx, p.x + Math.cos(a) * leafLen * 0.45, p.y + Math.sin(a) * leafLen * 0.45, leafLen, leafLen * 0.3, a);
      });
      ctx.fillStyle = layer.leaf;
      ctx.fill();
      strokeRim(ctx, rgba(rimOf(layer.leaf), 0.8), size, 0.6);
      if (layer.flower) {
        const tip = pts[pts.length - 1]!;
        flower(ctx, tip.x, tip.y, size.unit * 0.016, layer.flower);
        const mid = pts[Math.floor(pts.length * (0.4 + 0.3 * hash01(v.seed, 1)))]!;
        flower(ctx, mid.x + size.unit * 0.01, mid.y, size.unit * 0.012, layer.flower);
      }
    }
  },
};
